/**
 * The Electron (Windows desktop) adapter.
 *
 * Electron can do the two things the browser cannot: write a PDF to a chosen
 * path with no dialog, and print without the browser dialog. Both go through
 * the preload bridge, which is the renderer's ONLY route to Node — there is no
 * `import { ipcRenderer }` here, and there must never be, because this same
 * bundle is served to browsers where that import does not exist.
 *
 * THE BRIDGE IS OPTIONAL
 * ----------------------
 * Detection is by capability, not by a build flag: if `window.dnr` is absent,
 * the factory hands back the browser port. That is what lets one bundle serve
 * both targets, and it means a broken preload degrades to "the web experience"
 * rather than to a blank screen.
 *
 * The HTML still crosses unchanged. The main process waits for fonts to settle
 * in its own hidden window before printing — the renderer cannot check fonts
 * for a document it is not displaying, which is why that wait lives in
 * `main.cjs` and not here.
 */
import {
  PrintPortError,
  type PrintCapabilities,
  type PrintPort,
  type PrintRequest,
  type PrintResult,
  type PdfRequest,
  type PdfResult,
} from './printPort';

/** The subset of the preload bridge this adapter uses. */
export interface ElectronBridge {
  print(html: string): Promise<unknown>;
  saveAsPdf(fileName: string, html: string): Promise<unknown>;
}

export const ELECTRON_CAPABILITIES: PrintCapabilities = {
  platform: 'electron',
  interactivePrint: true,
  silentPdf: true,
  directThermal: false,
};

/** The bridge, if this process has one. Never assume it exists. */
export function electronBridge(): ElectronBridge | undefined {
  const g = globalThis as { dnr?: ElectronBridge };
  return typeof g.dnr?.print === 'function' && typeof g.dnr?.saveAsPdf === 'function'
    ? g.dnr
    : undefined;
}

export function createElectronPrintPort(bridge: ElectronBridge): PrintPort {
  return {
    capabilities: ELECTRON_CAPABILITIES,

    async print(request: PrintRequest): Promise<PrintResult> {
      try {
        await bridge.print(request.document.html);
        return { via: 'electron-print', printed: true };
      } catch (cause) {
        throw new PrintPortError(
          'PRINT_FAILED',
          `The desktop print failed: ${cause instanceof Error ? cause.message : String(cause)}`,
        );
      }
    },

    async savePdf(request: PdfRequest): Promise<PdfResult> {
      const name = request.fileName ?? request.document.fileName;
      try {
        const result = (await bridge.saveAsPdf(name, request.document.html)) as
          | { saved?: boolean; filePath?: string }
          | undefined;
        // The main process reports a cancelled dialog rather than throwing:
        // cancelling a save is a normal thing for a user to do, not a failure.
        if (result?.saved === false) return { via: 'electron-silent-pdf', saved: false };
        return { via: 'electron-silent-pdf', saved: true, filePath: result?.filePath };
      } catch (cause) {
        throw new PrintPortError(
          'PRINT_FAILED',
          `The desktop PDF failed: ${cause instanceof Error ? cause.message : String(cause)}`,
        );
      }
    },
  };
}